import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Activity, Send } from 'lucide-react'
import { fetchWithTimeout, getCurrentUser, parseJsonResponse, setCsrfToken } from '@/lib/api'

function sanitizeRedirect(rawRedirect: string | null): string {
  const raw = rawRedirect?.trim()
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return '/'

  const withoutLegacyPrefix = raw.replace(/^\/app(?=\/|\?|#|$)/, '')
  if (!withoutLegacyPrefix.startsWith('/') || withoutLegacyPrefix.startsWith('//')) return '/'

  const redirect = withoutLegacyPrefix
  const pathname = redirect.split(/[?#]/, 1)[0]

  if (pathname === '/login') return '/'
  return redirect
}

export default function Login() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [telegramEnabled, setTelegramEnabled] = useState(false)
  const [telegramStatusLoaded, setTelegramStatusLoaded] = useState(false)
  const [telegramUnavailableReason, setTelegramUnavailableReason] = useState('')
  const submitInFlight = useRef(false)

  const redirect = sanitizeRedirect(searchParams.get('redirect'))

  useEffect(() => {
    const telegramError = searchParams.get('telegram_error')
    if (telegramError) setError(telegramError)
    fetchWithTimeout('/api/auth/telegram/status', {
      credentials: 'include',
      cache: 'no-store',
    })
      .then(async (response) => {
        const data = await parseJsonResponse(response)
        if (!response.ok) throw new Error(data.detail || 'Telegram login status is unavailable.')
        setTelegramEnabled(Boolean(data.enabled))
        setTelegramUnavailableReason(
          data.enabled
            ? ''
            : data.message || 'Telegram login is not configured on this server.'
        )
      })
      .catch(() => {
        setTelegramEnabled(false)
        setTelegramUnavailableReason(
          'Could not check Telegram login. Make sure the Coin Parser backend is running.'
        )
      })
      .finally(() => setTelegramStatusLoaded(true))
  }, [searchParams])

  const handleTelegramLogin = () => {
    if (!telegramStatusLoaded) return
    if (!telegramEnabled) {
      setError(telegramUnavailableReason || 'Telegram login is not available.')
      return
    }
    window.location.href = `/api/auth/telegram/start?redirect=${encodeURIComponent(redirect)}`
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitInFlight.current) return
    submitInFlight.current = true
    setError('')
    setIsLoading(true)

    try {
      const response = await fetchWithTimeout('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        credentials: 'include',
        cache: 'no-store',
      })

      const data = await parseJsonResponse(response)

      if (!response.ok) {
        setError(data.error || data.detail || 'Login failed')
        return
      }

      // Store CSRF token if provided
      if (data.csrf_token) {
        setCsrfToken(data.csrf_token)
      }

      const verifiedUser = await getCurrentUser()
      if (!verifiedUser) {
        setError('Login succeeded, but the session could not be established. Please try again.')
        return
      }

      queryClient.setQueryData(['auth', 'me'], verifiedUser)

      // Redirect to the original page or dashboard
      navigate(redirect, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect to server')
    } finally {
      submitInFlight.current = false
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <Activity className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl">Coin Parser</CardTitle>
          <CardDescription>
            Enter your credentials to access the dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
              {error}
            </div>
          )}
          <Button
            type="button"
            variant={telegramEnabled ? 'default' : 'outline'}
            className="w-full"
            disabled={!telegramStatusLoaded}
            onClick={handleTelegramLogin}
          >
            <Send className="mr-2 h-4 w-4" />
            {telegramStatusLoaded ? 'Continue with Telegram' : 'Checking Telegram login...'}
          </Button>
          {telegramStatusLoaded && !telegramEnabled && (
            <p className="text-xs text-muted-foreground" role="status">
              {telegramUnavailableReason}
            </p>
          )}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" />
            <span>or use an existing password account</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="username" className="text-sm font-medium">
                Username
              </label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Password
              </label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
